import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { Link } from "@/i18n/routing";

const buttonVariants = cva(['cta'], {
    variants: {
      variant: {
        ['0']: 'layout-0',
        ['1']: 'layout-1',
        ['2']: 'layout-2',
      },
      size: {
        default: 'size-m',
        small: 'size-s',
        large: 'size-l',
        xlarge: 'size-xl',
      },
      icon: {
        default: 'icon-right',
        left: 'icon-left',
        none: 'icon-none',
      }
    },
    defaultVariants: {
      variant: '0',
      size: 'default',
      icon: 'none',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  href?: string | object;
  target?: string;
  scroll?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
  colorswoosh?: 'default' | 'outline';
  buttontext?: string;
}

const Button = ({ className, variant, size, href, icon, ref, ...props }: ButtonProps) => {
  const Comp = href ? Link : "button";

  return (
    <>
      {props.colorswoosh ? (
        <Comp
        /* @ts-expect-error link/button type */
        ref={ref}
        href={href as string}
        className={clsx('cta-swoosh', props.colorswoosh === 'outline' && 'layout-2', size === 'small' && 'size-s', icon === 'left' && 'icon-left', className)}
        {...props}
        >
          <span className="cta-swoosh-bg">
            <span className="cta-swoosh-bg-inner"></span>
            <span className="cta-swoosh-bg-inner"></span>
          </span>
          <span data-text={props.buttontext} className="cta-swoosh-inner">
            <span className="cta-swoosh-text">{props.children}</span>
            <span className="cta-swoosh-text-sec">{props.children}</span>
          </span>
        </Comp>
      ): (
        <Comp
          className={clsx(buttonVariants({ variant, size, icon, className }))}
          /* @ts-expect-error link/button type */
          ref={ref}
          href={href as string}
          {...props}
        />
      )}
    </>
  )
}
Button.displayName = "Button"

interface ButtonTypes {
  children: React.ReactNode;
  className?: string;
  variant?: string;
  size?: string;
  href?: string;
  alignment?: string;
  vertical?: boolean;
  target?: string;
  rel?: string;
};

const ButtonContainer = ({ children, alignment = 'left', vertical = false, className }: ButtonTypes) => {
  const alignmentClass = `alignment-${alignment}`;
  const verticalClass = vertical ? 'vertical-container' : 'horizontal-container';
  return (
    <div className={clsx('cta-container', alignmentClass, verticalClass, className)}>
      {children}
    </div>
  );
};
ButtonContainer.displayName = "ButtonContainer";

export { Button, ButtonContainer, buttonVariants }

//
// Example usage:
// <ButtonContainer alignment="left">
//   <Button href="/">Button as Link</Button>
// </ButtonContainer>
// <ButtonContainer alignment="center">
//   <Button>Button</Button>
// </ButtonContainer>
//